import { ApolloError } from "@apollo/client";
import { Alert, Box, Text } from "@chakra-ui/react";
import React from "react";
import { useParams } from "react-router-dom";
import { ApolloError as ApolloErrorView } from "src/components/Feedback/ApolloError";
import { InfoAlert } from "src/components/Feedback/InfoAlert";
import { NotFound } from "src/components/Feedback/NotFound";
import { OrganizationError as OrganizationErrorView } from "src/components/Feedback/OrganizationError";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import {
  FormContext,
  FormProvider,
  FormTemplateContext,
} from "src/components/Providers/FormProvider";
import { useLoadGooglePlacesScript } from "src/hooks/useLoadGoogleMaps";
import {
  OrganizationError,
  isOrganizationError,
} from "src/hooks/useOrganization";
import { usePreviousFormSchools } from "src/hooks/usePreviousFormSchools";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { failure, success, toTuple5 } from "src/types/remoteData";
import { EnforceFormEditability } from "./EnforceFormEditability";
import { Step } from "./Step";
import { StudentHeader } from "./components/Layout/StudentHeader";
import { Loading } from "./components/Loading";
import { CLEAR_IMPORT_SOURCE } from "./graphql/mutations";
import {
  GET_FORM_ANSWERS_BY_ID,
  GET_FORM_BY_ID,
  GET_SCHOOLS_RANK,
} from "./graphql/queries";

function handleError(
  error: Error | ApolloError | OrganizationError
): React.ReactElement {
  if (error instanceof ApolloError) {
    return <ApolloErrorView error={error} />;
  }

  if (isOrganizationError(error)) {
    return <OrganizationErrorView error={error} />;
  }

  return <Alert status="error">{error.message}</Alert>;
}

export const Edit = () => {
  const { formId = "" } = useParams();

  return (
    <FormProvider formId={formId}>
      {(formTemplateContextRD, formContextRD) => {
        return (
          <EditInternal
            formTemplateContextRD={formTemplateContextRD}
            formContextRD={formContextRD}
          />
        );
      }}
    </FormProvider>
  );
};

type Props = {
  formTemplateContextRD: RD.RemoteData<Error, FormTemplateContext>;
  formContextRD: RD.RemoteData<Error, FormContext>;
};
const EditInternal: React.FC<Props> = ({
  formTemplateContextRD,
  formContextRD,
}) => {
  const { formId = "", step: stepString = "0" } = useParams();
  const step = parseInt(stepString);
  const { isLoaded: isGoogleMapsApiLoaded } = useLoadGooglePlacesScript();

  const [clearImportSource] = useRemoteDataMutation<
    GQL.ClearImportSource,
    GQL.ClearImportSourceVariables
  >(CLEAR_IMPORT_SOURCE, { variables: { form_id: formId } });

  React.useEffect(() => {
    clearImportSource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { remoteData: gqlData } = useRemoteDataQuery<
    GQL.GetFormById,
    GQL.GetFormByIdVariables
  >(GET_FORM_BY_ID, {
    variables: { form_id: formId },
    fetchPolicy: "no-cache",
  });

  const remoteData = React.useMemo(() => {
    return gqlData
      .mapError<ApolloError | Error>((error) => error)
      .andThen((data) => {
        const form = data.form[0];
        if (!form?.form_template) {
          return failure<ApolloError | Error, GQL.GetFormById_form>(
            new Error("Form not found") // TODO: better error message
          );
        }
        return success<ApolloError, GQL.GetFormById_form>(form);
      });
  }, [gqlData]);

  const { remoteData: answersRemoteData } = useRemoteDataQuery<
    GQL.GetFormAnswersById,
    GQL.GetFormAnswersByIdVariables
  >(GET_FORM_ANSWERS_BY_ID, {
    fetchPolicy: "network-only",
    skip: Number.isNaN(step),
    variables: { form_id: formId },
  });

  const { remoteData: schoolRanksRemoteData, refetch: refetchSchoolRanks } =
    useRemoteDataQuery<GQL.GetSchoolsRank, GQL.GetSchoolsRankVariables>(
      GET_SCHOOLS_RANK,
      {
        fetchPolicy: "no-cache",
        skip: Number.isNaN(step),
        variables: { form_id: formId },
      }
    );

  const previousFormSchools = usePreviousFormSchools(formId);

  if (isNaN(step)) {
    return <NotFound />;
  }

  if (!isGoogleMapsApiLoaded) return <Loading />;

  return (
    <RemoteDataView
      error={handleError}
      loading={<Loading />}
      reloading={null}
      remoteData={toTuple5(
        remoteData,
        answersRemoteData,
        previousFormSchools,
        formTemplateContextRD,
        formContextRD
      )}
    >
      {([
        form,
        answers,
        previousFormSchools,
        formTemplateContext,
        formContext,
      ]) => {
        return (
          <EnforceFormEditability
            loading={<Loading />}
            formId={formId}
            formContext={formContext}
            formTemplateContext={formTemplateContext}
          >
            {(editMode) => (
              <Step
                formTemplateId={form.form_template.id}
                answers={answers}
                headers={
                  <>
                    <Box mx={4} mt={4}>
                      <StudentHeader
                        student={form.person}
                        enrollmentPeriodName={
                          form.form_template.enrollment_period.name
                        }
                      />
                    </Box>
                    {editMode === "BackInProgress" && (
                      <Box mx={4} mt={4} alignSelf="center" maxWidth="600px">
                        <InfoAlert
                          children={
                            <Text>
                              Form is back in progress. Don't forget to
                              re-submit!
                            </Text>
                          }
                        />
                      </Box>
                    )}
                  </>
                }
                currentStep={step}
                form={form}
                refetchSchoolRanks={refetchSchoolRanks}
                schoolRanksRemoteData={schoolRanksRemoteData}
                previousFormSchoolIds={previousFormSchools}
                sections={formTemplateContext.formTemplate.sections}
                formId={form.id}
                applicant={form.person}
                verificationResults={form.form_verification_results}
                hasBeenSubmittedBefore={form.submitted_before}
              />
            )}
          </EnforceFormEditability>
        );
      }}
    </RemoteDataView>
  );
};
